import React, { useState, useEffect, useContext, useRef } from 'react';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import axios from 'axios';
import Product from './Product';
import { GlobalContext } from '../context/GlobalState';
import { productTypeLabel } from '../utils/categoryLabels';
import { strings } from 'localisation';
import PropTypes from 'prop-types';

const LinkedProducts = ({ productCode, excludeId }) => {
  const { lang } = useContext(GlobalContext);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    el.addEventListener('scroll', updateScrollState);
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', updateScrollState);
    };
  }, [linkedProducts, loading]);

  // Compact rail cards: a full-size 4/5 product card is far too tall alongside the detail page.
  const cardWidth = isMobile ? 168 : 212;

  const scroll = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const gapPx = typeof theme.spacing(2) === 'number' ? theme.spacing(2) : 16;
    const step = Math.round(cardWidth + gapPx);
    el.scrollBy({ left: direction === 'left' ? -step : step, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchLinkedProducts = async () => {
      if (!productCode) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { data: response } = await axios.get(`/linkedProducts/`, {
          params: {
            code: productCode,
            excludeId: excludeId
          }
        });

        // Sort images by position (smallest order first) for each product
        if (response && Array.isArray(response)) {
          response.forEach((product) => {
            if (product.images && Array.isArray(product.images)) {
              product.images.sort(function (a, b) {
                const posA = a?.Product_Image?.position ?? a?.position ?? 999;
                const posB = b?.Product_Image?.position ?? b?.position ?? 999;
                return posA - posB;
              });
            }
          });
        }

        setLinkedProducts(response || []);
      } catch (error) {
        console.error('Error fetching linked products:', error);
        setLinkedProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLinkedProducts();
  }, [productCode, excludeId]);

  if (loading || linkedProducts.length === 0) {
    return null;
  }

  return (
    <Container style={{ paddingTop: 20, paddingBottom: 20 }}>
      <Box sx={{ mb: { xs: 4, md: 5.25 } }}>
        <Typography variant="overline" sx={{ display: 'block', mb: 1.75, color: 'gold.text' }}>
          {strings[lang]?.linkedProductsEyebrow || 'Разгледайте още'}
        </Typography>
        {/* The rail sits outside the product view's original-font scope on purpose, so
            this title reads in the theme's own display face (Playfair) like every other
            section heading in the redesign. */}
        <Typography variant="h2">{strings[lang]?.linkedProducts || 'Свързани продукти'}</Typography>
      </Box>
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0 }}>
        {canScrollLeft && (
          <IconButton
            onClick={() => scroll('left')}
            aria-label="Предишни продукти"
            sx={{
              position: 'absolute',
              left: -8,
              zIndex: 1,
              backgroundColor: 'background.default',
              border: '1px solid',
              borderColor: 'hairline.strong',
              '&:hover': {
                backgroundColor: 'background.paper',
                borderColor: 'text.primary'
              },
              [theme.breakpoints.down('sm')]: {
                left: 0
              }
            }}>
            <ChevronLeft />
          </IconButton>
        )}
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            overflowX: 'auto',
            overflowY: 'hidden',
            gap: 2,
            py: 1,
            px: 1,
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              height: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#F8F8F6',
              borderRadius: '10px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#DEDCD6',
              borderRadius: '10px',
              '&:hover': {
                background: '#5D5B56'
              }
            }
          }}>
          {linkedProducts.map((product) => (
            <Box
              key={product.id}
              sx={{
                width: cardWidth,
                minWidth: cardWidth,
                flexShrink: 0,
                scrollSnapAlign: 'start',
                cursor: 'pointer'
              }}>
              <Product
                Product={product}
                compact
                productTypeName={productTypeLabel(product.subCategoryId)}
              />
            </Box>
          ))}
        </Box>
        {canScrollRight && (
          <IconButton
            onClick={() => scroll('right')}
            aria-label="Следващи продукти"
            sx={{
              position: 'absolute',
              right: -8,
              zIndex: 1,
              backgroundColor: 'background.default',
              border: '1px solid',
              borderColor: 'hairline.strong',
              '&:hover': {
                backgroundColor: 'background.paper',
                borderColor: 'text.primary'
              },
              [theme.breakpoints.down('sm')]: {
                right: 0
              }
            }}>
            <ChevronRight />
          </IconButton>
        )}
      </Box>
    </Container>
  );
};

LinkedProducts.propTypes = {
  productCode: PropTypes.string,
  excludeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default LinkedProducts;
